import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import BreadCrumb from "Common/BreadCrumb";
import { PaginationState } from "@tanstack/react-table";
import TableContainer from "Common/TableContainer";
import { Link, useNavigate } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import HTMLReactParser from "html-react-parser";
import Modal from "Common/Components/Modal";
import {
  MoreHorizontal,
  Eye,
  FileEdit,
  Trash2,
  UploadCloud,
  ExternalLink,
} from "lucide-react";
import { Dropdown } from "Common/Components/Dropdown";
import {
  Download,
  Plus,
  Search,
  Pencil,
  SlidersHorizontal,
} from "lucide-react";
import DeleteModal from "Common/DeleteModal";
import callApi from "Common/utils/apiCaller";
import _ from "lodash";
import { getImageUrl } from "Common/utils/ImageDomain";
import { createSelector } from "reselect";
import { useSelector, useDispatch } from "react-redux";
import PermissionSwitch from "../../Common/Components/PermissionLocks/PermissionSwitch";
import { showNotification } from "Common/utils/Notification";
import { ToastContainer } from "react-toastify";
import { NEWS_ADD_ROUTE, NEWS_UPDATE_ROUTE } from "constants/routeEnum";
import options from "../../constants/published.json";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import Select from "react-select";

const News = () => {
  const selectProperties = createSelector(
    (state: any) => state.Login,
    (profile) => ({
      permission: profile.permission,
      user: profile.user,
    })
  );
  const [edit, setEdit] = useState<any>();
  const [option, setOption] = useState<any>([]);
  const [published, setPublished] = useState<any>();
  const [filters, setFilters] = useState<any>();
  const { permission, user } = useSelector(selectProperties);
  const [news, setNews] = useState([]);
  const navigate = useNavigate();
  const [read, setRead] = useState<any>();
  const [search, setSearch] = useState<any>();
  const [pageCount, setPageCount] = useState(0);
  const [eventData, setEventData] = useState<any>();
  const [totalItems, setTotalItems] = useState(0);
  const [render, setRender] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 30,
  });
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [showModel, setShowModel] = useState<boolean>(false);
  const defaultToggle = () => setShowModel(!showModel);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  const handleChange = (selectedOption: any) => {
    if (selectedOption.value === "Select...") {
      setPublished(null);
    } else if (selectedOption.value === "UnPublished") {
      let value1: boolean = false;
      setPublished(value1);
    } else if (selectedOption.value === "Published") {
      let value1: boolean = true;
      setPublished(value1);
    }
  };

  interface PermissionInfo {
    permissions: string[];
  }

  const rawPermission = localStorage.getItem("permission");
  const premissionInfo: PermissionInfo | null = rawPermission
  ? JSON.parse(rawPermission)
  : null;

  const debouncedHandler = useCallback(
    _.debounce(
      (
        user: any,
        render: any,
        pagination: any,
        published: any,
        dateFilter: any,
        edit: any
      ) => {
        return fetchDateWithFilters(
          user,
          render,
          pagination,
          published,
          dateFilter,
          edit
        );
      },
      700
    ),
    []
  );

  async function fetchDateWithFilters(
    searchByName: any,
    render: any,
    pagination: any,
    published: any,
    dateFilter: any,
    edit: any
  ) {
    let filters: { [index: string]: any } = {
      is_version_copy: false,
    };
    if (searchByName) {
      filters.qtext = searchByName;
    }
    if (published === true) {
      filters.is_published = published;
    } else if (published === false) {
      filters.is_published = published;
    }
    if (edit) {
      filters.is_edit = edit;
    } else if (edit === false) {
      filters.is_edit = edit;
    }
    if (dateFilter) {
      filters.published_from = moment(dateFilter.startDate)
        .startOf("day")
        .toDate();
      filters.published_to = moment(dateFilter.endDate).endOf("day").toDate();
    }
    const localData = await callApi("v1/news/list", "post", {
      pageNum: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filters,
    });

    if (localData?.status === "Success") {
      setNews([...localData.data.newsList]);
      setPageCount(Math.ceil(localData.data.newsCount / pagination.pageSize));
      setTotalItems(localData.data.newsCount);
    }
  }

  const filterSearchData = (e: any) => {
    const search = e.target.value;

    setSearch(search);
  };

  useEffect(() => {
    debouncedHandler(search, render, pagination, published, filters, edit);
  }, [search, render, pagination, published, filters, edit]);

  // Delete Data
  const onClickDelete = (cell: any) => {
    setDeleteModal(true);
    if (cell._id) {
      setEventData(cell);
    }
  };

  useEffect(() => {
    fetchData("edit_code");
  }, []);

  const fetchData = async (code) => {
    try {
      const res = await callApi(`v1/content/code/${code}`, "get", {});
      if (res.status === "Success") {
        setOption(res.data.content.params);
      }
    } catch (e) {
      showNotification("Error occured", "error");
    }
  };

  const handleEditChange = (selectedOption: any) => {
    if (selectedOption.value === "select") {
      setEdit(null);
    } else if (selectedOption.value === "unedit") {
      let value1: boolean = false;
      setEdit(value1);
    } else if (selectedOption.value === "edit") {
      let value1: boolean = true;
      setEdit(value1);
    }
  };

  const handleDelete = async () => {
    if (eventData) {
      const res = await callApi(`v1/news/${eventData?._id}/update`, "post", {
        news: { is_deleted: true },
      });
      if (res && res?.status === "Success") {
        setDeleteModal(false);
        setRender(!render);
        showNotification("Deleted successfully", "success");
      }
    }
  };

  const newsColumns = useMemo(
    () => [
      {
        header: "Heading",
        accessorKey: "heading",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "News Date",
        accessorKey: "released_at",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <div>
            {cell.row.original.released_at
              ? moment(cell.row.original.released_at).format("DD-MM-YYYY")
              : "No Date"}
          </div>
        ),
      },
      // {
      //   header: "Thumbnail",
      //   accessorKey: "thumbnail_url",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: (cell: any) => {
      //     return (
      //       <div>
      //         <Link
      //           to={getImageUrl(cell.row.original.thumbnail_url)}
      //           className="flex items-center gap-3 pl-4"
      //           target="_blank"
      //         >
      //           <div className="rounded-full size-6 shrink-0 bg-slate-100">
      //             <img
      //               src={getImageUrl(cell.row.original.thumbnail_url)}
      //               alt="img"
      //               className="size-6"
      //             />
      //           </div>
      //         </Link>
      //       </div>
      //     );
      //   },
      // },
      {
        header: "Link",
        accessorKey: "external_link",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <Link to="#!" className="flex items-center gap-3">
            <div>
              {HTMLReactParser(
                cell.row.original.external_link?.slice(0, 30) ?? ""
              )}
              {cell.row.original.external_link?.length > 30 ? "..." : ""}
              {cell.row.original.external_link && (
                <a
                  onClick={() => {
                    window.open(cell.row.original.external_link, "_blank");
                  }}
                  className="inline-flex items-center ml-1 text-custom-500 hover:text-custom-600"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </Link>
        ),
      },
      // {
      //   header: "Text",
      //   accessorKey: "text",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: (cell: any) => (
      //     <Link to="#!" className="flex items-center gap-3">
      //       <div>
      //         {HTMLReactParser(cell.row.original.text?.slice(0, 30) ?? "")}

      //         {cell.row.original.text?.length > 30 ? (
      //           <>
      //             {" ..."}
      //             <span
      //               style={{
      //                 color: "#fb801d",
      //               }}
      //               onClick={() => {
      //                 setRead(cell.row.original.text);
      //                 defaultToggle();
      //               }}
      //             >
      //               more
      //             </span>
      //           </>
      //         ) : null}
      //       </div>
      //     </Link>
      //   ),
      // },
      {
        header: "Status",
        accessorKey: "is_published",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell) => <>{cell.getValue() ? "Published" : "UnPublished"}</>,
      },
      {
        header: "Edit Status",
        accessorKey: "is_edit",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell) => <>{cell.getValue() ? "Edited" : "UnEdited"}</>,
      },
      {
        header: "Edited By",
        accessorKey: "edited_by",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const { edited_by } = row.original;

          return (
            <div>
              <span>{edited_by?.contact_name || ""}</span>
            </div>
          );
        },
      },
      {
        header: "Edit At",
        accessorKey: "updated_at",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const { updated_at } = row.original;

          const date = new Date(updated_at);
          const formattedDate = date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <div>
              <span>{updated_at && (formattedDate || "")}</span>
            </div>
          );
        },
      },
      {
        header: "Approved By",
        accessorKey: "approved_by",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const { approved_by } = row.original;

          return (
            <div>
              <span>{approved_by?.contact_name || ""}</span>
            </div>
          );
        },
      },
      {
        header: "Approved At",
        accessorKey: "approved_at",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const { approved_at } = row.original;

          const date = new Date(approved_at);
          const formattedDate = date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <div>
              <span>{approved_at && (formattedDate || "")}</span>
            </div>
          );
        },
      },
      {
        header: "Published By",
        accessorKey: "published_by",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const { published_by } = row.original;

          return (
            <div>
              <span>{published_by?.contact_name || ""}</span>
            </div>
          );
        },
      },
      {
        header: "Published At",
        accessorKey: "published_at",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const { published_at } = row.original;

          const date = new Date(published_at);
          const formattedDate = date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <div>
              <span>{published_at && (formattedDate || "")}</span>
            </div>
          );
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <div className="flex gap-3">
            <PermissionSwitch
              hasPermissions={premissionInfo?.permissions ?? []}
              shouldHavePermissions={["news:edit"]}
              yes={
                <div
                  data-modal-target="addEmployeeModal"
                  className="flex items-center justify-center transition-all duration-200 ease-linear rounded-md cursor-pointer size-8 edit-item-btn bg-slate-100 text-slate-500 hover:text-custom-500 hover:bg-custom-100 dark:bg-zink-600 dark:text-zink-200 dark:hover:bg-custom-500/20 dark:hover:text-custom-500"
                  onClick={() => {
                    const data = cell.row.original;
                    navigate(`${NEWS_UPDATE_ROUTE.replace(":id", data._id)}`);
                  }}
                >
                  <Pencil className="size-4" />
                </div>
              }
              no={<span></span>}
            />
            <PermissionSwitch
              hasPermissions={premissionInfo?.permissions ?? []}
              shouldHavePermissions={["news:delete"]}
              yes={
                <Link
                  to="#!"
                  className="flex items-center justify-center transition-all duration-200 ease-linear rounded-md size-8 remove-item-btn bg-slate-100 text-slate-500 hover:text-custom-500 hover:bg-custom-100 dark:bg-zink-600 dark:text-zink-200 dark:hover:bg-custom-500/20 dark:hover:text-custom-500"
                  onClick={() => {
                    const data = cell.row.original;
                    onClickDelete(data);
                  }}
                >
                  <Trash2 className="size-4" />
                </Link>
              }
              no={<span></span>}
            />
          </div>
        ),
      },
    ],
    []
  );

  return (
    <>
      <ToastContainer />
      <React.Fragment>
        <BreadCrumb title="news" pageTitle="news" version="" />
        <DeleteModal
          show={deleteModal}
          onHide={deleteToggle}
          onDelete={handleDelete}
        />
        {/* <ToastContainer closeButton={false} limit={1} /> */}
        <div className="grid grid-cols-12 gap-x-5">
          <div className="col-span-12 card 2xl:col-span-12">
            <div className="card-body">
              <div className="flex items-center">
                <h6 className="text-15 grow">News</h6>
                <div className="shrink-0">
                  <PermissionSwitch
                    hasPermissions={premissionInfo?.permissions ?? []}
                    shouldHavePermissions={["news:create"]}
                    yes={
                      <button
                        type="button"
                        className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                        onClick={() => navigate(NEWS_ADD_ROUTE)}
                      >
                        <Plus className="inline-block size-4" />{" "}
                        <span className="align-middle">Add News</span>
                      </button>
                    }
                    no={
                      <button
                        type="button"
                        disabled={true}
                        className="text-grey btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                        onClick={null}
                      >
                        <Plus className="inline-block size-4" />{" "}
                        <span className="align-middle">Add News</span>
                      </button>
                    }
                  />
                </div>
              </div>
            </div>
            <div className="!py-3.5 card-body border-y border-dashed border-slate-200 dark:border-zink-500">
              <form action="#!">
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
                  <div className="relative xl:col-span-3">
                    <input
                      type="text"
                      className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Search for news..."
                      autoComplete="off"
                      onChange={(e) => filterSearchData(e)}
                    />
                    <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                  <div className="xl:col-span-2">
                    <Select
                      className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      options={options}
                      isSearchable={false}
                      defaultValue={options[0]}
                      onChange={(event: any) => handleChange(event)}
                      id="choices-single-default"
                    />
                  </div>
                  {option?.length !== 0 && (
                    <div className="xl:col-span-2">
                      <Select
                        className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        options={option}
                        isSearchable={false}
                        defaultValue={option[0]}
                        onChange={(event: any) => handleEditChange(event)}
                        id="choices-single-default"
                      />
                    </div>
                  )}
                  <div className="xl:col-span-3 xl:col-start-10">
                    <div className="flex gap-2 xl:justify-end">
                      <Flatpickr
                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        options={{
                          dateFormat: "d M, Y",
                          mode: "range",
                        }}
                        placeholder="Select date range"
                        name="date"
                        onChange={(date) => {
                          date.length > 1 &&
                            setFilters((e) => ({
                              ...e,
                              startDate: date[0],
                              endDate: date[1],
                            }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="card-body">
              <TableContainer
                isPagination={true}
                columns={newsColumns || []}
                data={news || []}
                pageCount={pageCount}
                pagination={pagination}
                totalItems={totalItems}
                onPaginationChange={(updater: any) => {
                  const nextState = updater(pagination);
                  if (nextState.pageIndex !== pagination.pageIndex)
                    setPagination(nextState);
                }}
                divclassName="overflow-x-auto"
                tableclassName="w-full whitespace-nowrap"
                theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600"
                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500"
                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
              />
            </div>
          </div>
        </div>
        <Modal
          show={showModel}
          onHide={defaultToggle}
          id="defaultModal"
          modal-center="true"
          className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
          dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full"
        >
          <Modal.Header
            className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
            closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500"
          >
            <Modal.Title className="text-16">{"Text"}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
            {/* <h5 className="mb-3 text-16">{}</h5> */}
            <p className="text-slate-500 dark:text-zink-200">
              {HTMLReactParser(read ?? "")}
            </p>
          </Modal.Body>
        </Modal>
      </React.Fragment>
    </>
  );
};

export default News;
